import { Flex, SimpleGrid, Stack, Text, useTheme } from "@chakra-ui/react";
import Icon from "./ui-elements/Icon";
import Pluralize from "pluralize";
import React from "react";
import { SCHEDULE_DATA } from "../utils/data";
import { capFirstLetter, getLessonTime } from "../utils/functions";

type Props = {
  lessonType: string;
  lessonDuration: any;
  lessonHours: any;
  lessonTime: any;
  lessonUrgency: any;
  lessonDays: string[];
  client: any;
  [key: string]: any;
};

const LessonInformation = ({
  lessonType,
  lessonDuration,
  lessonHours,
  lessonTime,
  lessonUrgency,
  lessonDays = [],
  client = {},
  ...rest
}: Props) => {
  const {
    colors: { neutral },
  } = useTheme();
  const { daysOfTheWeek } = SCHEDULE_DATA;
  const timeOfLesson = getLessonTime({ lessonTime, lessonHours });
  const lessons = lessonDays.length * lessonDuration;
  const duration =
    lessonDuration === 4
      ? "per month"
      : `in ${Pluralize("week", lessonDuration, true)}`;

  function selectedDayProps(day: string, idx: number) {
    const gradient = `-55deg, ${neutral["200"]}, ${neutral["200"]} 10px, ${neutral["300"]} 10px, ${neutral["300"]} 20px`;

    let background = "inherit";
    let fontWeight = "inherit";
    let borderRightWidth = "none";

    if (lessonDays.includes(day)) {
      background = `repeating-linear-gradient(${gradient})`;
      fontWeight = "medium";
    }
    if (idx + 1 < daysOfTheWeek.length) borderRightWidth = "2xs";
    return { background, fontWeight, borderRightWidth };
  }
  const clientLocation = `${client.address}, ${client.vicinity}, ${client.region}`;
  const lessonLocation =
    lessonType === "physical" ? `Holds at ${clientLocation}` : "Online classes";
  const lessonIcon = lessonType === "physical" ? "pin" : "online";

  return (
    <Stack spacing={[5, 6]} {...rest}>
      <Stack spacing={1}>
        <Text fontSize="lg" fontWeight="medium">
          {capFirstLetter(lessonType)} lessons{" "}
          {Pluralize("day", lessonDays.length, true)} a week
        </Text>
        <Text opacity={0.7}>Lessons will hold on the days shaded</Text>
      </Stack>

      <Stack spacing={[5, 6]}>
        <Flex borderWidth="2xs" borderRadius="fbRadius" overflow="hidden">
          {daysOfTheWeek.map(({ shortDay, longDay }, idx) => (
            <Flex
              py={3}
              flex={1}
              key={longDay}
              align="center"
              justifyContent="center"
              fontSize={["sm", "md"]}
              {...selectedDayProps(longDay, idx)}
            >
              {shortDay}
            </Flex>
          ))}
        </Flex>

        <SimpleGrid columns={[1, 2]} spacing={4}>
          <Stack isInline align="center" spacing={[2, 3]}>
            <Icon name="time" size={[4, 5]} />
            <Text>From {timeOfLesson}</Text>
          </Stack>

          <Stack isInline align="center" spacing={[2, 3]}>
            <Icon name={lessonIcon} size={[4, 5]} />
            <Text>{lessonLocation}</Text>
          </Stack>

          <Stack isInline align="center" spacing={[2, 3]}>
            <Icon name="calendar-outline" size={[4, 5]} />
            <Text>
              {Pluralize("lesson", lessons, true)} {duration}
            </Text>
          </Stack>

          <Stack isInline align="center" spacing={[2, 3]}>
            <Icon name="notification" size={[4, 5]} />
            <Text>To start {lessonUrgency.toLowerCase()}</Text>
          </Stack>
        </SimpleGrid>
      </Stack>
    </Stack>
  );
};

export default LessonInformation;
